import { startOfDay, subWeeks } from 'date-fns';
import { useMemo } from 'react';

import { useCatalystInsightsList } from '~/react-query-hooks/insights/useCatalystInsights';
import { useEarningsInsightsFilter } from '~/react-query-hooks/insights/useEarningsInsightsFilter';
import { parseEarningsInsights } from '~/react-query-hooks/insights/useEarningsInsightsFilter.utils';
import { useFilteredInsights } from '~/react-query-hooks/insights/useFilteredInsights';
import { useFlags } from '~/stores/use-flags/useFlags';
import { useSubscription } from '~/stores/use-subscription/useSubscription';
import { METHOD_SUB_CLS_GROUPING } from '~/widgets/asset-overview-widget/method_sub_cls_grouping';

import { useAssetsTableStore } from '../../useAssetsTableStore';

const EARNINGS_INSIGHT_KEY = 'earnings';
const SCENARIO_INSIGHT_KEY = 'scenario';
const KG_INSIGHT_KEY = 'kg';

export interface UseInsightsCellDataParams {
  entityTag: string;
}

export const useInsightsCellData = ({
  entityTag,
}: UseInsightsCellDataParams) => {
  const {
    isCatalystCompanyInsightsEnabled,
    isCatalystThemeInsightsEnabled,
    isCatalystGeoInsightsEnabled,
    isCatalystMacroInsightsEnabled,
  } = useFlags(state => ({
    isCatalystCompanyInsightsEnabled: state.getFeatureFlagState(
      'catalysts_company_insights'
    ),
    isCatalystThemeInsightsEnabled: state.getFeatureFlagState(
      'catalysts_theme_insights'
    ),
    isCatalystGeoInsightsEnabled: state.getFeatureFlagState(
      'catalysts_geo_insights'
    ),
    isCatalystMacroInsightsEnabled: state.getFeatureFlagState(
      'catalysts_macro_insights'
    ),
  }));
  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );
  const insightsFilterState = useAssetsTableStore(
    state => state.insightsFilterState
  );
  const isMarketCatalystInsightsEnabled =
    isCatalystThemeInsightsEnabled ||
    isCatalystGeoInsightsEnabled ||
    isCatalystMacroInsightsEnabled;

  const fromDate = useMemo(() => startOfDay(subWeeks(new Date(), 1)), []);

  const { data: earningsInsightsData = [] } = useEarningsInsightsFilter({
    body: { entities: [entityTag], from: fromDate.toISOString() },
    isEnabled: !isBasicOrLightUser,
  });
  const { data, isLoading } = useFilteredInsights({ entity: [entityTag] });
  const { data: catalystInsights } = useCatalystInsightsList(
    {
      entities: isCatalystCompanyInsightsEnabled ? [entityTag] : [],
      categories: [
        ...(isCatalystCompanyInsightsEnabled ? (['company'] as const) : []),
        ...(isMarketCatalystInsightsEnabled ? (['market_wide'] as const) : []),
      ],
      from: fromDate.toISOString(),
    },
    isCatalystCompanyInsightsEnabled || isMarketCatalystInsightsEnabled
  );

  const earningsInsight = useMemo(
    () => parseEarningsInsights(earningsInsightsData),
    [earningsInsightsData]
  );

  const filteredScenarioInsights = useMemo(() => {
    const allAssetInsights = data?.data?.result ?? [];
    const scenarioInsightsFilterState =
      insightsFilterState[SCENARIO_INSIGHT_KEY];

    if (
      Object.values(scenarioInsightsFilterState).every(item => item.checked)
    ) {
      return allAssetInsights;
    }

    return allAssetInsights.filter(insight => {
      const analysisType = METHOD_SUB_CLS_GROUPING[insight.metadata.topic];
      return analysisType
        ? scenarioInsightsFilterState.find(item => item.key === analysisType)
            ?.checked
        : false;
    });
  }, [data?.data?.result, insightsFilterState]);

  const filteredCatalystInsights = useMemo(() => {
    const kgInsightsFilterState = insightsFilterState[KG_INSIGHT_KEY];

    return catalystInsights?.filter(catalystInsight => {
      const analysisType = METHOD_SUB_CLS_GROUPING[catalystInsight.category];
      return analysisType
        ? kgInsightsFilterState.find(item => item.key === analysisType)?.checked
        : false;
    });
  }, [catalystInsights, insightsFilterState]);

  const showEarningsInsight = useMemo(() => {
    const kgInsightsFilterState = insightsFilterState[KG_INSIGHT_KEY];
    return kgInsightsFilterState.find(item => item.key === EARNINGS_INSIGHT_KEY)
      ?.checked;
  }, [insightsFilterState]);

  return {
    isLoading,
    filteredScenarioInsights,
    filteredCatalystInsights,
    earningsInsight: showEarningsInsight ? earningsInsight : undefined,
  };
};
